/**
 * Binding to the browser's frame and task scheduling APIs (`requestAnimationFrame` and
 * `scheduler.postTask`): run a callback after the next frame has been painted.
 *
 * This is the one after-paint primitive in the web app. Screens use it to start their refresh
 * requests and "refreshing" state after a reveal or mount has painted; `useAfterPaint` uses it to
 * lag a value by one painted frame; `AfterFirstPaint` and the task editor use it to build
 * secondary dialog sections after the dialog's first frame. It holds no decisions; callers decide
 * what to run.
 *
 * @module apps/web/src/lib/afterPaint
 */

/** Task priorities this binding queues at, from the Prioritized Task Scheduling API. */
export type AfterPaintPriority = 'background' | 'user-visible';

/** The parts of the Prioritized Task Scheduling API this binding uses. */
interface PostTaskScheduler {
  /** Queue `callback` as a task at `priority`. */
  postTask: (
    callback: () => void,
    options: {
      /** Task priority. */ priority: AfterPaintPriority;
      /** Cancels the task. */ signal: AbortSignal;
    }
  ) => Promise<void>;
}

/** Options of `afterNextPaint`. */
export interface AfterPaintOptions {
  /**
   * Animation frames to wait before queueing the task (default 1, minimum 1). Use 2 when the
   * callback must not run before the answering frame is presented: the first frame callback can
   * run before it.
   */
  frames?: number;
  /**
   * Priority of the queued task (default `'background'`). `'user-visible'` runs it as an ordinary
   * task, for work the user is about to look at, such as a dialog's remaining fields.
   */
  priority?: AfterPaintPriority;
}

/**
 * Run `callback` in a task queued from the `frames`-th next animation frame, which is after that
 * frame paints. Uses `scheduler.postTask` at `priority`, and falls back to `setTimeout(0)` where
 * that API is missing. Returns a function that cancels the callback if it has not run yet; call
 * it from an effect's cleanup.
 */
export function afterNextPaint(
  callback: () => void,
  { frames = 1, priority = 'background' }: AfterPaintOptions = {}
): () => void {
  let cancelled = false;
  let frame = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const abort = new AbortController();
  const run = () => {
    if (!cancelled) callback();
  };
  const queue = () => {
    const scheduler = (globalThis as { /** Present in Chromium. */ scheduler?: PostTaskScheduler })
      .scheduler;
    if (scheduler?.postTask)
      scheduler.postTask(run, { priority, signal: abort.signal }).catch(() => {});
    else timer = setTimeout(run, 0);
  };
  const wait = (remaining: number) => {
    frame = requestAnimationFrame(() => (remaining > 1 ? wait(remaining - 1) : queue()));
  };
  wait(Math.max(1, frames));
  return () => {
    cancelled = true;
    cancelAnimationFrame(frame);
    abort.abort();
    if (timer !== undefined) clearTimeout(timer);
  };
}
